import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProfile } from '../api/settings';
import './ProfileMenuButton.css';

// Initials for the avatar (first + last initial of the display name, else the
// first letter of the email). Mirrors ProfilePage's placeholder avatar.
function initialsFrom(name, email) {
  const src = (name || '').trim();
  if (src) {
    const parts = src.split(/\s+/);
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }
  return ((email || '?').trim().charAt(0) || '?').toUpperCase();
}

// Account dropdown in the Dashboard header (GitHub-style). The avatar trigger opens
// a menu with the identity header + links (Profile, Team/Organization, Settings) and
// Sign out — it does NOT navigate on its own. Self-contained: fetches the profile for
// the name/email/initials. Sign out lives here (Dashboard-only), via onLogout.
export default function ProfileMenuButton({ onLogout }) {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState({ displayName: '', email: '' });

  useEffect(() => {
    getProfile()
      .then((p) => setProfile({ displayName: p.displayName || '', email: p.email || '' }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // The three destinations below are `<Link>`s, so ctrl/⌘+click and middle-click
  // open them in a new tab. The menu closes on any activation — it is transient
  // chrome, and the overlay dismisses it anyway.
  const close = () => setOpen(false);

  const initials = initialsFrom(profile.displayName, profile.email);

  return (
    <div className="profile-menu">
      <button
        type="button"
        className="profile-menu-trigger"
        onClick={() => setOpen((o) => !o)}
        title="Account"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {initials}
      </button>

      {open && (
        <>
          <div className="profile-menu-overlay" onClick={() => setOpen(false)} />
          <div className="profile-menu-dropdown" role="menu">
            <div className="profile-menu-identity">
              <div className="profile-menu-avatar" aria-hidden="true">
                {initials}
              </div>
              <div className="profile-menu-meta">
                <div className="profile-menu-name">{profile.displayName || 'Unnamed'}</div>
                <div className="profile-menu-email">{profile.email || '—'}</div>
              </div>
            </div>

            <div className="profile-menu-sep" />

            <Link
              className="applink profile-menu-item"
              role="menuitem"
              to="/profile"
              onClick={close}
            >
              <span className="profile-menu-icon">👤</span> Profile
            </Link>
            <Link className="applink profile-menu-item" role="menuitem" to="/admin" onClick={close}>
              <span className="profile-menu-icon">👥</span> Team &amp; Organization
            </Link>
            <Link
              className="applink profile-menu-item"
              role="menuitem"
              to="/settings"
              onClick={close}
            >
              <span className="profile-menu-icon">⚙</span> Settings
            </Link>

            <div className="profile-menu-sep" />

            <button
              className="profile-menu-item profile-menu-item-danger"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
            >
              <span className="profile-menu-icon">⏻</span> Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
